import { useEffect, useState, useRef } from 'react';
import { Theme } from '@mui/system';
import { makeStyles } from '@mui/styles';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { Card, CardContent, List, ListItem, CardHeader, Fab, CircularProgress, Fade, useTheme } from '@mui/material';
import { formatDate } from '@ts/ui-models';
import { Grid, GridContainerFullHeight, Icon, Typography } from '@ts/storybook';
import { debounce } from 'lodash';

const useStyles = makeStyles<Theme, { theme: Theme }>(() => {
  const defaultStyle = { maxWidth: '300px', height: '69px', transition: 'width 0.5s ease-in-out, height 0.5s ease-in-out' };
  const clicked = { width: '600px', height: '170px', transition: 'width 0.5s ease-in-out, height 0.5s ease-in-out' };

  return {
    cardLocation: props => ({
      borderRadius: '16px',
      border: '2px solid ' + props.theme.palette.primary.light,
      position: 'relative',
      //cursor: 'pointer',
      ...defaultStyle,
    }),
    cardSelected: props => ({
      border: '2px solid ' + props.theme.palette.primary.main,
      ...clicked,
    }),
    cardHeader: props => ({
      border: '5px solid ' + props.theme.palette.primary.light,
      borderRadius: '12px',
      textAlign: 'center',
    }),
    headerSelected: props => ({
      borderRadius: '12px 12px 0 0',
      border: '5px solid ' + props.theme.palette.primary.main,
    }),
    fabBtnToUp: {
      position: 'absolute',
      bottom: 0,
      right: '20px',
    },
  };
});

interface TimelineItemProps {
  id: string | number;
  title: string;
  description: string;
  timestamp: string;
  createAt: string;
  icon?: string;
  //selected?: boolean;
  disabled?: boolean;
}

interface ITimelineProps {
  listTimeline: TimelineItemProps[];
  canMoreFetch?: boolean;
  loading?: boolean;
  onChangeEndScroll?: () => void;
}

const TTimeline = (props: ITimelineProps) => {
  const theme = useTheme();
  const classesStyle = useStyles({ theme });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [showFabBtnToUp, setShowFabBtnToUp] = useState(false);
  const [listTimeline, setListTimeline] = useState<TimelineItemProps[]>([]);
  const [timelineSelected, setTimelineSelected] = useState<TimelineItemProps | null>();

  //#region Handlers
  const onClickScrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    setTimeout(() => {
      setShowFabBtnToUp(false);
    }, 1000);
  };
  /*   const onClickTimeline = (item: TimelineItemProps) => {
    if (!item.disabled && timelineSelected?.id !== item.id) {
      setTimelineSelected(item);
    } else {
      setTimelineSelected(null);
    }
  }; */
  //#endregion

  //#region Render
  const renderCardLocation = (item, index) => {
    const isItemSelected = timelineSelected?.id === item.id;
    const isEven = index % 2 !== 0;
    const alignmentStyle = isEven ? { marginLeft: 'auto', marginRight: 0 } : { marginRight: 'auto', marginLeft: 0 };

    return (
      <Card
        className={`${classesStyle.cardLocation} ${isItemSelected && classesStyle.cardSelected}`}
        sx={{
          ...alignmentStyle,
        }}
        //onClick={() => onClickTimeline(item)}
      >
        <CardHeader
          className={`${classesStyle.cardHeader} ${isItemSelected && classesStyle.headerSelected}`}
          title={item.title}
        />
        {isItemSelected && (
          <CardContent>
            <List>
              <ListItem></ListItem>
            </List>
          </CardContent>
        )}
      </Card>
    );
  };
  const renderTimelineItem = (item: TimelineItemProps, index) => {
    const isFirstAndLast = index === 0 || index === listTimeline.length - 1;
    const isLast = index === listTimeline.length - 1;
    return (
      <TimelineContent
        sx={{
          py: '12px',
          px: 2,
          m: isFirstAndLast ? '' : 'auto 0',
          ...(isLast ? { display: 'flex', flexDirection: 'column-reverse' } : {}),
        }}
      >
        {renderCardLocation(item, index)}
      </TimelineContent>
    );
  };
  const renderOpositeTimelineItem = (item: TimelineItemProps, index) => {
    const isFirstAndLast = index === 0 || index === listTimeline.length - 1;
    const isLast = index === listTimeline.length - 1;
    return (
      item.timestamp && (
        <TimelineOppositeContent
          sx={{
            paddingTop: index === 0 ? '16px' : '',
            paddingBottom: isLast ? '10px' : '',
            m: 'auto 0',
            height: isFirstAndLast ? '100px' : 'auto',
            ...(isLast ? { display: 'flex', flexDirection: 'column-reverse' } : {}),
          }}
          //variant="h6"
          //color="text.primary"
        >
          {formatDate(item.timestamp)}
        </TimelineOppositeContent>
      )
    );
  };
  const renderTimeLineConnector = (isLast?: boolean) => {
    return (
      <TimelineConnector
        sx={{
          width: '3px',
          height: '40px',
          ...(isLast ? { marginBottom: '10px' } : {}),
        }}
      />
    );
  };
  const renderLoading = () => {
    if (props.canMoreFetch && listTimeline.length !== 0) {
      return (
        <Fade key={'loading'} in={true} style={{ transformOrigin: 'center top' }}>
          <TimelineItem>
            <TimelineOppositeContent></TimelineOppositeContent>
            <TimelineSeparator>
              {renderTimeLineConnector(true)}
              <CircularProgress />
            </TimelineSeparator>
            <TimelineContent></TimelineContent>
          </TimelineItem>
        </Fade>
      );
    } else {
      return [];
    }
  };
  const renderTimelineList = () => {
    return listTimeline
      ?.map((item, i) => {
        return (
          <Fade key={item.id} in={true} timeout={(i % 10) * 300} style={{ transformOrigin: 'center top' }}>
            <TimelineItem>
              {renderOpositeTimelineItem(item, i)}
              <TimelineSeparator sx={{ alignSelf: 'center' }}>
                {i !== 0 && renderTimeLineConnector()}
                <TimelineDot
                  color={item.disabled ? 'grey' : 'primary'}
                  variant={item.id === timelineSelected?.id ? 'filled' : 'outlined'}
                >
                  <Icon icon={item.icon || 'locationOn'} color={item.disabled ? 'grey' : ''} />
                </TimelineDot>
                {i !== listTimeline.length - 1 && renderTimeLineConnector()}
              </TimelineSeparator>
              {renderTimelineItem(item, i)}
            </TimelineItem>
          </Fade>
        );
      })
      .concat(renderLoading());
  };
  //#endregion

  useEffect(() => {
    if (props.listTimeline) {
      setListTimeline(props.listTimeline);
    }
  }, [props.listTimeline]);

  //HandleScroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleDebouncedScroll = debounce((event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollTop > 10 && !showFabBtnToUp) {
        setShowFabBtnToUp(true);
      }

      if (scrollTop + clientHeight >= scrollHeight - 50 && !props.loading) {
        props.onChangeEndScroll?.();
      }
    }, 200);

    scrollContainer.addEventListener('scroll', handleDebouncedScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleDebouncedScroll);
      handleDebouncedScroll.cancel();
    };
  }, [props.loading, showFabBtnToUp, scrollContainerRef]);

  if (listTimeline.length === 0 && !props.loading)
    return (
      <GridContainerFullHeight horizontalAlign="center" verticalAlign="center">
        <Typography variant="h4" color="gray" text="No history" />
      </GridContainerFullHeight>
    );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Grid
        ref={scrollContainerRef}
        id="timeline-container"
        container
        sx={{ position: 'relative', height: '100%', overflowY: 'auto' }}
      >
        <Timeline position="alternate">{renderTimelineList()}</Timeline>
      </Grid>
      {showFabBtnToUp && (
        <Fab className={classesStyle.fabBtnToUp} variant="extended" color="primary" onClick={onClickScrollToTop}>
          <Icon size={18} icon="navigation" />
        </Fab>
      )}
    </div>
  );
};

export default TTimeline;
